import { ChangeDetectorRef, Component, EventEmitter, Input, Output, OnInit, Type, ViewContainerRef } from '@angular/core';
import { MdDialog, MdDialogConfig, MdDialogRef } from '@angular/material';
import {Router, ActivatedRoute} from '@angular/router';

import { Task } from 'app/shared/models/task.model';
import { Comment } from 'app/shared/models/comment.model';
import { CookieService } from 'app/services/cookie.service';
import { ConstantsService } from 'app/services/constants-service';

import { SnackBarService } from 'app/services/snackbar.service';
import { MessageService } from 'app/services/messages.service';
import { SidebarTasksService } from 'app/services/sidebar-tasks.service';
import { WarmUpService } from 'app/services/warmup/warmup.service';
import { NavigationBarService } from 'app/header/navigationbar/navigation-bar.service';

import { AddTaskComponent } from 'app/task/addtask/add-task.component';
import { DelegateTaskComponent } from 'app/task/delegatetask/delegate-task.component';
import { TaskGuideComponent } from 'app/task/taskguide/task-guide.component';

import { TaskChoices } from 'app/shared/models/task.model';
import { User } from 'app/shared/models/user.model';

import { TaskService } from 'app/task/task.service';
import { formatDisplayDate, formatDisplayDateTime } from 'app/shared/utils/date-format.util';

@Component({
  selector: 'task-details',
  templateUrl: './task-details.component.html',
  styleUrls: ['./task-details.component.css']
})

/**
 * Main component for task details.
 */
export class TaskDetailsComponent implements OnInit {

  @Input() hideClient = true;
  @Input() showTaskActions = true;
  @Input() taskDetails: Task;

  @Output() updatedTaskDetails: EventEmitter<Object> = new EventEmitter();

  dialogRef: MdDialogRef<any>;
  taskChoicesValueSet = new Array<string>();
  assigneeName: string;
  loginUser: string;
  previousAssignee: string;
  users: User[];

  selectedEntityId: string;
  selectedEntityType: string;

  hasTaskChoices = false;
  hasDelegationState = false;
  showTaskOptions = false;
  replyViewSelected: boolean;
  commentsExpanded: boolean;

  // Constructor method
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private dialog: MdDialog,
    private cdr: ChangeDetectorRef,
    private cookieService: CookieService,
    private snackBarService: SnackBarService,
    private viewContainerRef: ViewContainerRef,
    private warmUpService: WarmUpService,
    private navigationBarService: NavigationBarService,
    private sidebarTasksService: SidebarTasksService,
    private taskService: TaskService) { }

  ngOnInit() {
    this.replyViewSelected = false;
    this.taskDetails.newComment = new Comment();
    this.taskDetails.newComment.text = '';
    if (this.taskDetails.taskDefId) {
      this.showTaskOptions = true;
    }
    if (this.taskDetails.taskChoices) {
      this.hasTaskChoices = true;
      const taskChoices = this.taskDetails.taskChoices.choices;
      Object.keys(taskChoices).forEach(key => {
        this.taskChoicesValueSet.push(taskChoices[key]);
      });
      this.cdr.detectChanges();
    }
    if (this.taskDetails.delegationState === 'PENDING') {
      this.hasDelegationState = true;
    }
    this.loginUser = this.cookieService.get(ConstantsService.COOKIE_LOGIN_USER_NAME);
    this.previousAssignee = this.taskDetails.assignee;
    this.setAssigneeName(this.warmUpService.getUsersList());

    this.selectedEntityType = this.cookieService.get(ConstantsService.ENT_CUR_TYPE);
    this.selectedEntityId = this.cookieService.get(ConstantsService.ENT_CUR_ID);
  }

  setAssigneeName(users: User[]) {
    this.users = users;

    if (this.users && this.taskDetails.assignee != null) {
      const user = this.users.find(u => u.loginId === this.taskDetails.assignee);

      if (user != null) {
        this.assigneeName = user.name;
        this.assigneeName = this.assigneeName === '' ? this.taskDetails.assignee : this.assigneeName;
      }
    }
  }

  formatDate(dateField) {
    if (!dateField) {
      return 'Not set';
    }
    return formatDisplayDate(dateField) || 'Not set';
  }

  formatDateTime(dateField) {
    if (!dateField) {
      return '';
    }
    return formatDisplayDateTime(dateField);
  }

  getCommentsLength() {
    if (!this.taskDetails.comments) {
      return 0;
    }
    return this.taskDetails.comments.length;
  }

  showFirstCommentSection() {
    this.taskDetails.addFirstComment = true;
  }

  showReplyView() {
    this.replyViewSelected = true;
  }

  showAllComments() {
    this.commentsExpanded = true;
  }

  showDeleteOption(writer) {
    // TODO(naveenkiran): Commenting th code and returning false always as to disable this option.
    // Update accordingly once decision is taken for delete option.
    return false;
    // if (!writer) {
    //   return false;
    // }
    // return this.loginUser === writer;
  }

  popoutTask() {
    this.navigationBarService.setSelectedTask(this.taskDetails);

    // ensure that task panel gets closed when popping out a task
    this.sidebarTasksService.ensureSidebarIsClosed();
  }

  openEditTaskDialog(selectedTask: Task) {
    const config = new MdDialogConfig();
    config.disableClose = true;
    config.viewContainerRef = this.viewContainerRef;
    this.dialogRef = this.dialog.open(AddTaskComponent, config);
    this.dialogRef.componentInstance.action = 'Edit';
    this.dialogRef.componentInstance.taskData = this.taskDetails;
    this.dialogRef.afterClosed().subscribe(result => {
      if (result) {
        if (this.previousAssignee !== result.assignee) {
          if (result.assignee === this.loginUser) {
            this.updatedTaskDetails.emit({ prevTab: 'OtherTasks', newTab: 'MyTasks', task: result });
          } else if (this.previousAssignee === this.loginUser) {
            this.updatedTaskDetails.emit({ prevTab: 'MyTasks', newTab: 'OtherTasks', task: result });
          } else {
            this.updatedTaskDetails.emit({ prevTab: 'OtherTasks', newTab: 'OtherTasks', task: result });
          }
        } else {
          this.taskDetails = result;
          this.taskDetails.newComment = new Comment();
          this.taskDetails.newComment.text = '';
        }
      }
      this.dialogRef = null;
    });
  }

  openDelegateTaskDialog(selectedTask: Task) {
    const config = new MdDialogConfig();
    config.disableClose = true;
    config.viewContainerRef = this.viewContainerRef;
    this.dialogRef = this.dialog.open(DelegateTaskComponent, config);
    this.dialogRef.componentInstance.taskData = this.taskDetails;
    this.dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.updatedTaskDetails.emit({ prevTab: 'MyTasks', newTab: 'OtherTasks', task: result.arrResult });
      }
      this.dialogRef = null;
    });
  }

  openGuideTextDialog() {
    const config = new MdDialogConfig();
    config.disableClose = true;
    config.viewContainerRef = this.viewContainerRef;
    this.dialogRef = this.dialog.open(TaskGuideComponent, config);
    this.dialogRef.componentInstance.title = this.taskDetails.title;
    this.dialogRef.componentInstance.taskDefId = this.taskDetails.taskDefId;
    this.dialogRef.afterClosed().subscribe(result => {
      this.dialogRef = null;
    });
  }

  addComment() {
    if (this.taskDetails.newComment.text === '') {
      return;
    }
    const updatedTask = new Task();
    updatedTask.id = this.taskDetails.id;
    updatedTask.comments = [this.taskDetails.newComment];
    this.taskService
      .addComment(updatedTask)
      .subscribe(response => {
        if (response.flagResult) {
          this.taskDetails.comments = response.arrResult.comments;
          this.taskDetails.newComment = new Comment();
          this.taskDetails.newComment.text = '';
        }
        this.callSnackBarService(response,
          MessageService.ADD_COMMENT_SUCCESS,
          MessageService.ADD_COMMENT_FAILURE);
      },
      err => {
        this.callSnackBarService(err,
          MessageService.ADD_COMMENT_SUCCESS,
          MessageService.ADD_COMMENT_FAILURE);
      });
  }

  removeComment(comment: Comment) {
    this.taskService.removeComment(comment.id)
      .subscribe(response => {
        if (response.flagResult) {
          this.taskDetails.comments.splice(this.taskDetails.comments.indexOf(comment), 1);
        }
        this.callSnackBarService(response,
          MessageService.REMOVE_COMMENT_SUCCESS,
          MessageService.REMOVE_COMMENT_FAILURE);
      },
      err => {
        this.callSnackBarService(err,
          MessageService.REMOVE_COMMENT_SUCCESS,
          MessageService.REMOVE_COMMENT_FAILURE);
      });
  }

  submitTask(taskDetails: Task) {
    const loginUser = this.cookieService.get(ConstantsService.COOKIE_LOGIN_USER_NAME);

    if (loginUser !== taskDetails.assignee && loginUser !== taskDetails.owner) {
      window.alert('Only the owner or assignee can close this task.');
      return;
    }
    const task = new Task();
    task.assignee = taskDetails.assignee;
    task.id = taskDetails.id;
    task.owner = taskDetails.owner;
    task.comments = taskDetails.comments;
    // @todo add a 'busy processing' icon.
    this.taskService.submitTask(task)
      .subscribe(response => {
        if (response.flagResult) {
          this.taskDetails.isSubmitted = true;
          this.updatedTaskDetails.emit(this.taskDetails);
        }
        this.callSnackBarService(response,
          MessageService.TASK_SUBMIT_SUCCESS,
          MessageService.TASK_SUBMIT_FAILURE);
      },
      err => {
        this.callSnackBarService(err,
          MessageService.TASK_SUBMIT_SUCCESS,
          MessageService.TASK_SUBMIT_FAILURE);
      });
  }

  resolveTask(taskDetails: Task) {
    const loginUser = this.cookieService.get(ConstantsService.COOKIE_LOGIN_USER_NAME);

    if (loginUser !== taskDetails.assignee && loginUser !== taskDetails.owner) {
      window.alert('Only the owner or assignee can close this task.');
      return;
    }

    this.taskService.resolveTask({ id: taskDetails.id })
      .subscribe(response => {
        if (response.flagResult) {
          this.taskDetails.isSubmitted = true;
          this.updatedTaskDetails.emit(this.taskDetails);
        }
        this.callSnackBarService(response,
          MessageService.TASK_SUBMIT_SUCCESS,
          MessageService.TASK_SUBMIT_FAILURE);
      },
      err => {
        this.callSnackBarService(err,
          MessageService.TASK_SUBMIT_SUCCESS,
          MessageService.TASK_SUBMIT_FAILURE);
      });
  }

  submitTaskChoice(choiceValue: string) {
    const loginUser = this.cookieService.get(ConstantsService.COOKIE_LOGIN_USER_NAME);

    if (loginUser !== this.taskDetails.assignee && loginUser !== this.taskDetails.owner) {
      window.alert('Only the owner or assignee can close this task.');
      return;
    }

    // Preparing the taskChoices basis the user selected choice
    const taskChoices = new TaskChoices();
    taskChoices.name = this.taskDetails.taskChoices.name;
    const name = taskChoices.name;
    const choices = { [name]: this.findTaskChoiceKey(choiceValue) };
    taskChoices.choices = choices;

    const task = new Task();
    task.assignee = this.taskDetails.assignee;
    task.id = this.taskDetails.id;
    task.owner = this.taskDetails.owner;
    task.comments = this.taskDetails.comments;
    task.taskChoices = taskChoices;
    // @todo add a 'busy processing' icon.
    this.taskService.submitTaskChoice(task)
      .subscribe(response => {
        if (response.flagResult) {
          this.taskDetails.isSubmitted = true;
          this.updatedTaskDetails.emit(this.taskDetails);
        }
        this.callSnackBarService(response,
          MessageService.TASK_SUBMIT_CHOICE_SUCCESS,
          MessageService.TASK_SUBMIT_CHOICE_FAILURE);
      },
      err => {
        const stringBody = err._body;
        const message = TaskDetailsComponent.messageErrParser(stringBody);
        this.callSnackBarService(JSON.parse(TaskDetailsComponent.messageErrParser(stringBody)).message,
          MessageService.TASK_SUBMIT_CHOICE_SUCCESS,
          MessageService.TASK_SUBMIT_CHOICE_FAILURE);

        setTimeout(() => {
          this.router.navigate(['/entity', this.selectedEntityId, 'compliance']);
          this.sidebarTasksService.toggleSidebar();
        }, 3000);

      });
  }

  private static messageErrParser(m: string): string {
    let out = '';
    const length = m.length - 1;
    for (let i = 0; i < m.length; i++) {
      if(i !== 11 && i !== (length - 1)) {
        out+=m.charAt(i);
      }
    }
    const jsonAux = JSON.parse(out);
    if(jsonAux.message.message && jsonAux.message.message.includes(':')) {
      const aux = jsonAux.message.message.split(':');
      jsonAux.message.message = aux[aux.length -1];
      out = JSON.stringify(jsonAux);
    }
     return out;
  }

  findTaskChoiceKey(choiceValue: string) {
    const taskChoices = this.taskDetails.taskChoices.choices;
    return Object.keys(taskChoices).find(key => taskChoices[key] === choiceValue);
  }

  loadCommentWriterName(commentWriter: string): string {
    if (this.users) {
      const writer = this.users.find(user => user.loginId === commentWriter);

      if (writer == null) {
        return null;
      }

      return writer.name === '' ? commentWriter : writer.name;
    }

    return null;
  }
  callSnackBarService(response: any, successMsg: string, failureMsg: string) {
    this.snackBarService.showStatus(response, successMsg, failureMsg);
  }
}



// WEBPACK FOOTER //
// ./src/app/task/taskdetails/task-details.component.ts